import React, { useState } from 'react';
import { connect, Provider } from 'react-redux';

import { IntlContextProvider, TranslatedMessage } from 'common/components/Intl';
import { changeQuickShopVisibility } from 'apps/cart/actions';
import store from 'apps/cart/store';
import flatMap from 'lodash/fp/flatMap';
import uniqBy from 'lodash/fp/uniqBy';
import map from 'lodash/fp/map';
import some from 'lodash/fp/some';
import get from 'lodash/fp/get';
import flow from 'lodash/fp/flow';
import pick from 'lodash/fp/pick';
import isEmpty from 'lodash/fp/isEmpty';

import PodFilters from 'common/components/shop/PodFilters';
import PriceSpan from 'common/components/formatters/PriceSpan';
import ProductListing from 'common/components/shop/ProductListing';
import ProductTile from 'common/components/shop/ProductTile';
import ShopButton from 'common/components/ShopButton';
import AddToCartButton from 'apps/cart/containers/AddToCartButton';

export const getPodFilterOptions = variantData => ({
  nicotineStrengthOptions: flow(
    flatMap(getFilterableValues),
    uniqBy('nicotineStrength'),
    map('nicotineStrength')
  )(variantData),
  podCountOptions: flow(
    flatMap(getFilterableValues),
    uniqBy('podCount'),
    map(pick(['podCount', 'price']))
  )(variantData),
});

const getFilterableValues = variant =>
  flow(
    get('quickShopOfferings'),
    map(pick(['nicotineStrength', 'podCount', 'price']))
  )(variant);

const showQuickShopModal = variant =>
  variant.quickShopOfferings && variant.quickShopOfferings.length > 0;

export const ProductListingContainer = ({
  openQuickShop,
  variantData,
  ...props
}) => {
  const { nicotineStrengthOptions, podCountOptions } = getPodFilterOptions(
    variantData
  );

  const showPodFilters =
    !isEmpty(nicotineStrengthOptions) || !isEmpty(podCountOptions);

  const showTilePriceTag = !showPodFilters;

  const [selectedFilters, setSelectedFilters] = useState({
    nicotineStrength: get('0', nicotineStrengthOptions),
    podCount: get('0.podCount', podCountOptions),
  });

  const handleOnChangeFilter = filters => {
    setSelectedFilters(currentSelectedFilters => ({
      ...currentSelectedFilters,
      ...filters,
    }));
  };

  const productTiles = variantData.reduce((tiles, variant) => {
    if (!variant.offerings.length) {
      return tiles;
    }

    const showVariant = variant =>
      !showPodFilters || some(selectedFilters)(getFilterableValues(variant));

    const callToAction = showQuickShopModal(variant) ? (
      <ShopButton color="light" onClick={() => openQuickShop(variant)}>
        <TranslatedMessage id="page.shop.add" />
      </ShopButton>
    ) : (
      // TODO: this just takes the first offering and adds it to the cart when
      // clicked. We need to be able to support multiple offerings and offering
      // types (Commitment rewards, subscription-only, etc).
      <AddToCartButton
        buttonComponent={ShopButton}
        isSoldOut={variant.offerings[0].is_sold_out}
        isSubscriberExclusive={variant.offerings[0].subscriber_exclusive}
        offeringId={variant.offerings[0].id}
        sku={variant.offerings[0].variant_sku}
        useQuantityInput={false}
      />
    );

    const pricingProp = showTilePriceTag
      ? { price: <PriceSpan amount={variant.price} /> }
      : {};

    const tile = (
      <ProductTile
        callToAction={callToAction}
        imageUrl={variant.imageUrl}
        key={variant.offerings[0].variant_sku}
        name={variant.name}
        starRating={variant.starRating}
        url={variant.url}
        {...pricingProp}
      />
    );

    if (showVariant(variant)) tiles.push(tile);

    return tiles;
  }, []);

  if (!productTiles.length) {
    return null;
  }

  return (
    <>
      {showPodFilters && (
        <PodFilters
          nicotineStrengthOptions={nicotineStrengthOptions}
          podCountOptions={podCountOptions}
          onChangeFilter={handleOnChangeFilter}
        />
      )}
      <ProductListing {...props}>{productTiles}</ProductListing>
    </>
  );
};

const mapDispatchToProps = dispatch => ({
  openQuickShop: variant => {
    dispatch(
      changeQuickShopVisibility(
        true,
        variant.quickShopOfferings,
        variant.hasTaxDisclaimer,
        variant.category
      )
    );
  },
});

export const ConnectedProductListingContainer = connect(
  null,
  mapDispatchToProps
)(ProductListingContainer);

const WrappedProductListing = props => (
  <Provider store={store}>
    <IntlContextProvider {...props.intlContext}>
      <ConnectedProductListingContainer {...props} />
    </IntlContextProvider>
  </Provider>
);

export default WrappedProductListing;
